// Retail TOU rows summed to one line per channel and band, for the Step 3 panel.
//
// bv_retail_tou_usage holds consumption (E) and export (B, migration 125) under
// the SAME band names, told apart by nmi_suffix alone — applyToBill already
// splits them. The panel summed them together, so a site with no solar showed
// "0 of 2 bands priced" and $0.00: every day's B1 rows are 0 kWh with no
// feed-in rate, and one null cost made the whole band unknown (NMI 60014106913,
// Sep 2026). On a solar site it would also have added export kWh to Peak.

import type { BvRetailTouUsage, BvTouBand } from '../types/billValidationTypes';

export type RetailBandChannel = 'consumption' | 'export';

export type RetailBandLine = {
  channel: RetailBandChannel;
  band: BvTouBand;
  qty: number;
  rate: number | null;
  /** Null when any day with usage in this band has no rate. */
  cost: number | null;
};

const BAND_ORDER: BvTouBand[] = ['superpeak', 'peak', 'shoulder', 'offpeak'];
const round2 = (n: number) => Math.round(n * 100) / 100;

export function rollupRetailBands(
  rows: Pick<BvRetailTouUsage, 'nmi_suffix' | 'band' | 'quantity' | 'rate' | 'cost'>[],
): RetailBandLine[] {
  const lines = new Map<string, RetailBandLine>();
  for (const r of rows) {
    const qty = Number(r.quantity);
    const channel: RetailBandChannel = (r.nmi_suffix ?? 'E').toUpperCase().startsWith('B') ? 'export' : 'consumption';
    const key = `${channel}:${r.band}`;
    const prev = lines.get(key) ?? { channel, band: r.band, qty: 0, rate: null, cost: 0 };
    // A day with nothing to price costs nothing, whatever its rate.
    const dayCost = qty === 0 ? 0 : r.cost == null ? null : Number(r.cost);
    lines.set(key, {
      channel,
      band: r.band,
      qty: prev.qty + qty,
      rate: r.rate ?? prev.rate,
      cost: prev.cost == null || dayCost == null ? null : round2(prev.cost + dayCost),
    });
  }
  return [...lines.values()]
    // An export channel with no export (no solar) is not a line.
    .filter((l) => l.channel === 'consumption' || l.qty !== 0)
    .sort((a, b) => (a.channel === b.channel ? 0 : a.channel === 'consumption' ? -1 : 1)
      || BAND_ORDER.indexOf(a.band) - BAND_ORDER.indexOf(b.band));
}
